// İSG uzmanı portalı — Taşerona saha erişimi ekleme (Modal + RHF + Zod). api-contract
// §13.4 (`POST /contractors/{uuid}/sites {siteUuid}`). Saha seçici firmanın sahalarından
// (§10.3 `GET /sites`) doldurulur; zaten atanmış sahalar seçenekten çıkarılır. Idempotent
// (backend zaten aktifse var-olanı döner). Saha yok/başka firma → `404 SITE_NOT_FOUND`.

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Field,
  Modal,
  Select,
  type SelectOption,
  Spinner,
  contractorsApi,
  resolveApiErrorMessage,
  settingsApi,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { contractorsKeys } from '../../lib/contractorsKeys';
import { settingsKeys } from '../../lib/settingsKeys';

/** Saha seçicisini tek sayfada besleyecek makul üst sınır (tenant ölçeği). */
const SITE_OPTIONS_PAGE_SIZE = 200;

interface SiteAccessModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contractorUuid: string;
  /** Zaten atanmış saha public kimlikleri — seçenekten çıkarılır. */
  assignedSiteUuids: string[];
  onSuccess?: () => void;
}

export function SiteAccessModal({
  open,
  onOpenChange,
  contractorUuid,
  assignedSiteUuids,
  onSuccess,
}: SiteAccessModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const sitesQuery = useQuery({
    queryKey: settingsKeys.sites({
      page: 1,
      pageSize: SITE_OPTIONS_PAGE_SIZE,
      sortBy: 'name',
      sortDir: 'asc',
    }),
    queryFn: () =>
      settingsApi.listSites({
        page: 1,
        pageSize: SITE_OPTIONS_PAGE_SIZE,
        sortBy: 'name',
        sortDir: 'asc',
      }),
    enabled: open,
    staleTime: 60_000,
  });

  const assigned = useMemo(() => new Set(assignedSiteUuids), [assignedSiteUuids]);
  const siteOptions: SelectOption[] = useMemo(
    () =>
      (sitesQuery.data?.items ?? [])
        .filter((s) => !assigned.has(s.uuid))
        .map((s) => ({
          value: s.uuid,
          label: s.branchName ? `${s.name} · ${s.branchName}` : s.name,
        })),
    [sitesQuery.data, assigned],
  );

  const schema = useMemo(
    () => z.object({ siteUuid: z.string().min(1, t('contractors.validation.siteRequired')) }),
    [t],
  );
  type FormValues = z.infer<typeof schema>;

  const { register, handleSubmit, reset, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { siteUuid: '' },
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      contractorsApi.addContractorSite(contractorUuid, { siteUuid: values.siteUuid }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractorsKeys.sites(contractorUuid) });
      reset();
      onSuccess?.();
      onOpenChange(false);
    },
  });

  const noSites = sitesQuery.isSuccess && siteOptions.length === 0;

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          mutation.reset();
          reset();
        }
        onOpenChange(next);
      }}
      title={t('contractors.sites.add.title')}
      description={t('contractors.sites.add.description')}
      size="md"
      closeLabel={t('contractors.common.close')}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('contractors.common.cancel')}
          </Button>
          <Button
            type="submit"
            form="site-access-form"
            loading={mutation.isPending}
            disabled={noSites}
          >
            {mutation.isPending ? (
              <Spinner label={t('contractors.common.add')} />
            ) : (
              t('contractors.common.add')
            )}
          </Button>
        </>
      }
    >
      <form
        id="site-access-form"
        className="flex flex-col gap-4"
        onSubmit={handleSubmit((values) => mutation.mutate(values))}
        noValidate
      >
        {mutation.isError ? (
          <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
        ) : null}

        {noSites ? <Alert tone="info">{t('contractors.sites.noSites')}</Alert> : null}

        <Field
          htmlFor="site-access-site"
          label={t('contractors.sites.field.site')}
          error={formState.errors.siteUuid?.message}
          required
        >
          <Select
            id="site-access-site"
            invalid={Boolean(formState.errors.siteUuid)}
            disabled={sitesQuery.isPending || noSites}
            placeholder={t('contractors.sites.sitePlaceholder')}
            options={siteOptions}
            {...register('siteUuid')}
          />
        </Field>
      </form>
    </Modal>
  );
}
